package fr.manyverse.app.presentation.components.soundboard

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MusicNote
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.SkipNext
import androidx.compose.material.icons.filled.Stop
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.Sound
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.crossfading
import manyverse.shared.generated.resources.next_track
import manyverse.shared.generated.resources.now_playing
import manyverse.shared.generated.resources.sound_stop_playback
import org.jetbrains.compose.resources.stringResource

/**
 * Overlay showing the currently playing sound with playback controls.
 *
 * @param currentSound The sound currently playing
 * @param isPlaying Whether playback is active
 * @param isCrossfading Whether a crossfade transition is in progress
 * @param progress Playback progress as a fraction from 0.0 to 1.0
 * @param onPlayPauseClick Callback when play/pause button is clicked
 * @param onStopClick Callback when stop button is clicked
 * @param modifier Modifier for the overlay
 */
@Composable
fun NowPlayingOverlay(
    currentSound: Sound?,
    isPlaying: Boolean,
    isCrossfading: Boolean,
    progress: Float,
    onPlayPauseClick: () -> Unit,
    onStopClick: () -> Unit,
    modifier: Modifier = Modifier,
    showNextTrackButton: Boolean = false,
    onNextTrackClick: () -> Unit = {}
) {
    // Animate progress for smooth transitions
    val animatedProgress by animateFloatAsState(
        targetValue = progress.coerceIn(0f, 1f),
        label = "progress"
    )

    AnimatedVisibility(
        visible = currentSound != null,
        enter = slideInVertically { it },
        exit = slideOutVertically { it },
        modifier = modifier
    ) {
        Surface(
            color = MaterialTheme.colorScheme.surfaceContainerHigh,
            shape = RoundedCornerShape(15.dp),
            tonalElevation = 8.dp
        ) {
            Column(
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 16.dp, end = 16.dp, top = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Music icon
                    Icon(
                        imageVector = Icons.Default.MusicNote,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary
                    )

                    // Sound info
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = stringResource(Res.string.now_playing),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = currentSound?.name ?: "",
                            style = MaterialTheme.typography.titleSmall
                        )
                        if (isCrossfading) {
                            Text(
                                text = stringResource(Res.string.crossfading),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.tertiary
                            )
                        }
                    }

                    // Play/Pause button
                    IconButton(onClick = onPlayPauseClick) {
                        Icon(
                            imageVector = if (isPlaying) Icons.Default.Pause else Icons.Default.PlayArrow,
                            contentDescription = if (isPlaying) "Pause" else "Play"
                        )
                    }

                    // Next track button (auto-play mode only)
                    if (showNextTrackButton) {
                        IconButton(onClick = onNextTrackClick) {
                            Icon(
                                imageVector = Icons.Default.SkipNext,
                                contentDescription = stringResource(Res.string.next_track)
                            )
                        }
                    }

                    // Stop button
                    IconButton(onClick = onStopClick) {
                        Icon(
                            imageVector = Icons.Default.Stop,
                            contentDescription = stringResource(Res.string.sound_stop_playback)
                        )
                    }
                }

                // Progress bar
                Spacer(modifier = Modifier.height(4.dp))
                LinearProgressIndicator(
                    progress = { animatedProgress },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(4.dp)
                        .padding(horizontal = 16.dp)
                        .clip(RoundedCornerShape(bottomStart = 15.dp, bottomEnd = 15.dp)),
                    color = MaterialTheme.colorScheme.primary,
                    trackColor = MaterialTheme.colorScheme.surfaceVariant,
                )
                Spacer(modifier = Modifier.height(8.dp))
            }
        }
    }
}
